import { useTranslation } from 'react-i18next';
import Table, { TableCell, SortableHeaderElement } from 'components/ui/Table';
import { BatchReturnOverview } from 'types/batchReturn';
import Text from 'components/ui/Text';
import styled from 'styled-components';

export type BatchReturnOrderByField = 'Id' | 'Created' | 'Status' | 'BatchDestinationName';

type BatchReturnTableProps = {
    items: BatchReturnOverview[];
    compact?: boolean;
    orderBy?: string;
    orderByDirection?: string;
    onSort: (field: BatchReturnOrderByField) => void;
    onRowClick?: (item: BatchReturnOverview) => void;
};

const BatchReturnTable = ({
    items,
    compact = false,
    orderBy,
    orderByDirection,
    onSort,
    onRowClick,
}: BatchReturnTableProps) => {
    const { t } = useTranslation();

    const headers: React.ReactNode[] = [
        <SortableHeaderElement
            label={t('batchReturn.id', 'ID')}
            active={orderBy === 'Id'}
            desc={orderByDirection === 'DESC'}
            onClick={() => onSort('Id')}
        />,
        <SortableHeaderElement
            label={t('batchReturn.destination', 'Destination')}
            active={orderBy === 'BatchDestinationName'}
            desc={orderByDirection === 'DESC'}
            onClick={() => onSort('BatchDestinationName')}
        />,
        <SortableHeaderElement
            label={t('batchReturn.created', 'Created')}
            active={orderBy === 'Created'}
            desc={orderByDirection === 'DESC'}
            onClick={() => onSort('Created')}
        />,
        t('batchReturn.createdBy', 'Created by'),
        t('batchReturn.comment', 'Comment'),
        t('batchReturn.itemCount', 'Items'),
    ];

    const rows = items.map((item) => ({
        key: String(item.id),
        onClick: onRowClick ? () => onRowClick(item) : undefined,
        cells: [
            { el: <Text small>#{item.id}</Text> },
            { el: <Text small>{item.batchDestinationName ?? '-'}</Text> },
            { el: <Text small>{new Date(item.created).toLocaleDateString()}</Text> },
            { el: <Text small>{item.createdByUser}</Text> },
            { el: <Text small>{item.comment ?? '-'}</Text> },
            {
                el: (
                    <Text small>
                        {item.itemCount}
                        {item.deviationCount > 0 && (
                            <DeviationText as="span">
                                {' '}
                                ({t('batchReturn.deviations', '{{count}} deviations', { count: item.deviationCount })})
                            </DeviationText>
                        )}
                    </Text>
                ),
                align: 'center' as const,
            },
        ],
    }));

    return (
        <Table
            className="batch-return-table"
            compact={compact}
            headers={headers}
            cols={headers.length}
            rows={rows}
            bgColor="fillWhite"
            arrow={!!onRowClick}
            arrowLabel={t('show', 'Show')}
        />
    );
};

export default BatchReturnTable;

const DeviationText = styled(Text)`
    font-size: inherit;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.error};
`;
